import { createQuery, request } from '@luna/api';
import { getEnv } from '@luna/env';
import { createSharedSlice } from '@luna/state';
import type { MyProfile } from '@shared/profile';
import { ProfileFlags, useMyProfile } from '@shared/profile';

export interface MultiProfileProduct {
    id: string;
    type?: string;
}

export type MultiProfileType = 'PHYSICAL_PERSON' | 'LEGAL_ENTITY' | 'OTHER_BANKS';

export interface MultiProfile {
    id: string;
    name: string;
    entityType?: MultiProfileType;
    entityRegistrationNumber?: string;
    products: MultiProfileProduct[];
}

interface UseMultiProfiles {
    isPending: boolean;
    isMultiProfileUser: boolean;
    isMultiProfileDisplayEnabled: boolean;
    multiProfiles: MultiProfile[];
    toggleProductGrouping: () => void;
}

const EMPTY_PROFILES: MultiProfile[] = [];

const initialState = {
    isSwitchActive: false,
};
const useSwitchState = createSharedSlice({
    initialState,
    reducers: {
        setSwitch: (state, action: { payload: boolean }) => {
            state.isSwitchActive = action.payload;
        },
        toggleSwitch: (state) => {
            state.isSwitchActive = !state.isSwitchActive;
        },
    },
});

const [useMultiProfilesApi] = createQuery({
    name: 'profile/multi-profiles',
    payloadCreator: async (profile?: MyProfile): Promise<MultiProfile[]> => {
        if (!profile?.flags?.includes(ProfileFlags.MULTIPLE_PROFILES)) {
            return EMPTY_PROFILES;
        }

        const response = await request<{ multipleProfiles?: MultiProfile[] }>(
            `${getEnv().prestoUrl}netbanking/my/profile/user-profiles`,
        );

        const multiProfiles = response?.multipleProfiles ?? EMPTY_PROFILES;
        const isMultiProfileDisplayEnabled =
            multiProfiles.length > 1 &&
            !!profile &&
            !profile.flags?.includes(ProfileFlags.MULTIPLE_PROFILES_DISPLAY_DISABLED);

        useSwitchState.dispatchActions.setSwitch(isMultiProfileDisplayEnabled);

        return multiProfiles;
    },
});

export function useMultiProfiles(): UseMultiProfiles {
    const { isPending: isProfilePending, data: profile } = useMyProfile();
    const { isPending: areMultiProfilesPending, data: multiProfiles = EMPTY_PROFILES } =
        useMultiProfilesApi(profile);
    const isMultiProfileUser = !!profile?.flags?.includes(ProfileFlags.MULTIPLE_PROFILES);
    const { isSwitchActive: isMultiProfileDisplayEnabled, dispatchActions } = useSwitchState();

    const toggleProductGrouping = (): void => {
        dispatchActions.toggleSwitch();
    };

    return {
        isPending: isProfilePending || areMultiProfilesPending,
        isMultiProfileUser,
        isMultiProfileDisplayEnabled,
        multiProfiles,
        toggleProductGrouping,
    };
}
